import axios from 'axios';
import {useIntl} from 'react-intl';
import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import SegmentationState from './states';
import URL_CONST from '../../../../config/url';
import {SegmentationDto} from './SegmentationDto';
import {getHeaders} from '../../../../store/dataService';
import {SegmentationListDto} from './SegmentationListDto';
import {ISegmentationDto} from './SegmentationDtoInterface';
import {toast} from '../../../../base-components/StudioToast';
import {ISegmentationListDto} from './SegmentationListDtoInterface';
import {Point, Polygon, BoundingBox} from './SegmentationDtoInterface';
import {AnnotationDataType} from '../../../../types/dataset/DatasetGetAnnotationsResponse';

interface SegmentationContextType {
  projectId: string | null;
  setProjectId: (projectId: string | null) => void;

  datasetId: string | null;
  setDatasetId: (datasetId: string | null) => void;

  dataPreparationContainerId: string | null;
  setDataPreparationContainerId: (dataPreparationContainerId: string | null) => void;

  shutdownSegmentationAPI: () => Promise<void>;
  initializeSegmentationAPI: () => Promise<void>;

  currentStates: SegmentationState[];
  resetState: () => void;
  addState: (state: SegmentationState) => void;
  removeState: (state: SegmentationState) => void;

  activeImageId: string | null;
  setActiveImageId: (imageId: string | null) => void;

  activePoint: Point | null;
  setActivePoint: (point: Point | null) => void;

  activeBoundingBox: BoundingBox | null;
  setActiveBoundingBox: (box: BoundingBox | null) => void;

  activePolygon: Polygon | null;
  setActivePolygon: (polygonId: Polygon | null) => void;

  activeSegmentation: ISegmentationDto | null;
  segmentationList: ISegmentationListDto;
  initializeANewPrediction: () => Promise<void>;
  updateActiveSegmentationResults(): Promise<void>;
  setInitialSegments: (initialSegments: AnnotationDataType) => void;
  getSegmentation: (imageId: string) => ISegmentationDto | undefined;
  clearSegmentationData: (imageId?: string) => Promise<void>;
  removePoint: (imageId: string, point: Point) => Promise<boolean>;
  addBoundingBox: (imageId: string, box: BoundingBox) => Promise<void>;
  removeBoundingBox: (imageId: string, id: string) => Promise<boolean>;
  addPoint: (imageId: string, point: Point, isPositive: boolean) => Promise<void>;
  createOrUpdateSegmentation: (
    imageId: string,
    segmentationData?: Partial<ISegmentationDto>
  ) => Promise<ISegmentationDto>;
  updateBoundingBox: (
    imageId: string,
    id: string,
    propertiesToUpdate: Partial<BoundingBox>
  ) => Promise<boolean>;
  removePolygon: (imageId: string, polygonId: Polygon['id']) => Promise<boolean>;
  updatePolygonLabel: (
    imageId: string,
    polygonId: Polygon['id'],
    label: Polygon['label']
  ) => Promise<boolean>;
}

const initialState: SegmentationContextType = {
  datasetId: null,
  setDatasetId: () => {},

  dataPreparationContainerId: null,
  setDataPreparationContainerId: () => {},

  projectId: null,
  setProjectId: () => {},

  shutdownSegmentationAPI: async () => {},
  initializeSegmentationAPI: async () => {},

  currentStates: [SegmentationState.IDLE],
  addState: () => {},
  resetState: () => {},
  removeState: () => {},

  activeImageId: null,
  setActiveImageId: () => {},

  activePolygon: null,
  setActivePolygon: () => {},

  activePoint: null,
  setActivePoint: () => {},

  activeBoundingBox: null,
  setActiveBoundingBox: () => {},

  activeSegmentation: null,
  segmentationList: new SegmentationListDto(),
  setInitialSegments: () => {},
  getSegmentation: () => undefined,
  removePolygon: async () => false,
  updateActiveSegmentationResults: async () => Promise.resolve(),
  createOrUpdateSegmentation: async () => new SegmentationDto(''),
  clearSegmentationData: async () => {},
  addPoint: async () => {},
  removePoint: async () => false,
  addBoundingBox: async () => {},
  updateBoundingBox: async () => false,
  removeBoundingBox: async () => false,
  updatePolygonLabel: async () => false,
  initializeANewPrediction: async () => {},
};

const SegmentationContext = createContext<SegmentationContextType>(initialState);

export const SegmentationProvider: React.FC = ({children}) => {
  const intl = useIntl();

  const [projectId, setProjectId] = useState<string | null>(null);
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [dataPreparationContainerId, setDataPreparationContainerId] = useState<
    string | null
  >(null);

  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const [activePolygon, setActivePolygon] = useState<Polygon | null>(null);
  const [activePoint, setActivePoint] = useState<Point | null>(null);
  const [activeBoundingBox, setActiveBoundingBox] = useState<BoundingBox | null>(null);
  const [segmentationList, setSegmentationList] = useState<ISegmentationListDto>(
    new SegmentationListDto()
  );

  const activeSegmentation = useMemo(() => {
    return segmentationList.getSegmentationByImageId(activeImageId || '') ?? null;
  }, [activeImageId, segmentationList]);

  const getCurrentSegmentationList = useCallback((): Promise<ISegmentationListDto> => {
    return new Promise<ISegmentationListDto>(resolve => {
      setSegmentationList((prevList: ISegmentationListDto) => {
        resolve(prevList);

        return prevList;
      });
    });
  }, []);

  const getCurrentActiveImage = useCallback((): Promise<string | null> => {
    return new Promise<string | null>(resolve => {
      setActiveImageId((latestActiveImageId: string | null) => {
        resolve(latestActiveImageId);

        return latestActiveImageId;
      });
    });
  }, []);

  const initializeSegmentationAPI = useCallback(async () => {
    if (dataPreparationContainerId && projectId) {
      try {
        const headers = await getHeaders({
          'Request-Timeout': 3000,
        });

        await axios.get(URL_CONST.DSS_SEGMENTATION_INITIALIZE(projectId), {
          params: {resourceId: dataPreparationContainerId},
          headers,
        });
      } catch (error) {
        throw error;
      }
    }
  }, [dataPreparationContainerId, projectId]);

  const shutdownSegmentationAPI = useCallback(async () => {
    if (dataPreparationContainerId && projectId) {
      try {
        const headers = await getHeaders({
          'Request-Timeout': 3000,
        });

        await axios.get(URL_CONST.DSS_SEGMENTATION_SHUTDOWN(projectId), {
          params: {resourceId: dataPreparationContainerId},
          headers,
        });
      } catch (error) {
        throw error;
      }
    }
  }, [dataPreparationContainerId, projectId]);

  // private
  const updateSegmentationResult = useCallback(
    function updateSegmentationResult(): Promise<{polygon: Point[]} | undefined> {
      return new Promise(async (resolve, reject) => {
        try {
          const latestACtiveImageId = await getCurrentActiveImage();

          if (latestACtiveImageId && dataPreparationContainerId && projectId) {
            const prevList = await getCurrentSegmentationList();
            const segmentation = prevList.getSegmentationByImageId(latestACtiveImageId);

            if (segmentation && segmentation.hasDataForPrediction()) {
              const headers = await getHeaders({
                'Request-Timeout': 3000,
              });

              const response = await axios.post(
                URL_CONST.DSS_SEGMENTATION_PREDICTIONS(projectId),
                {
                  image_id: Number(latestACtiveImageId),
                  positive_points: segmentation?.getPositivePoints(),
                  negative_points: segmentation?.getNegativePoints(),
                },
                {headers, params: {resourceId: dataPreparationContainerId}}
              );

              if (response.data?.error || response.data?.errors) {
                throw new Error(response.data.error);
              } else if (!response.data?.body?.polygon) {
                throw new Error('API response has an invalid contract for predictions');
              }

              resolve(response.data.body);
            } else {
              resolve({polygon: []});
            }
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [
      projectId,
      dataPreparationContainerId,
      getCurrentActiveImage,
      getCurrentSegmentationList,
    ]
  );

  const [currentStates, setCurrentStates] = useState<SegmentationState[]>([
    SegmentationState.IDLE,
  ]);

  const addState = useCallback((state: SegmentationState) => {
    setCurrentStates(prevStates => {
      return !prevStates.includes(state) ? [...prevStates, state] : prevStates;
    });
  }, []);

  const removeState = useCallback((state: SegmentationState) => {
    setCurrentStates(prevStates => prevStates.filter(prevState => prevState !== state));
  }, []);

  const resetState = useCallback(() => {
    setCurrentStates([SegmentationState.IDLE]);
  }, []);

  useEffect(() => {
    if (!currentStates.length) {
      addState(SegmentationState.IDLE);
    } else if (currentStates.length > 1) {
      removeState(SegmentationState.IDLE);
    }
  }, [currentStates, addState, removeState]);

  const setInitialSegments = useCallback(
    (initialData: AnnotationDataType) => {
      const {annotations, categories} = initialData;

      setSegmentationList(prevList => {
        const newList = prevList.clone();

        annotations.forEach(annotation => {
          const imageId = String(annotation.image_id);
          let segmentation = newList.getSegmentationByImageId(imageId);

          if (!segmentation) {
            segmentation = new SegmentationDto(imageId);
          }

          const points = annotation.segmentation;

          let label: string | undefined;
          const categoryMetadata = categories.find(
            category => category.id === annotation.category_id
          );

          if (categoryMetadata) {
            label = categoryMetadata.name;
          }

          const polygon: Partial<Polygon> = {
            points: points,
            label: label,
          };

          segmentation.addPolygon(polygon);
          newList.addOrUpdateSegmentation(imageId, segmentation);
        });

        return newList;
      });
    },
    [setSegmentationList]
  );

  const setActiveImageIdCallback = useCallback((imageId: string | null) => {
    setActiveImageId(imageId);
    setActivePolygon(null);
    setActivePoint(null);
    setActiveBoundingBox(null);
  }, []);

  const setActivePolygonCallback = useCallback((polygon: Polygon | null) => {
    setActivePolygon(polygon);
    setActivePoint(null);
    setActiveBoundingBox(null);
  }, []);

  const setActivePointCallback = useCallback((point: Point | null) => {
    setActivePoint(point);
    setActivePolygon(null);
    setActiveBoundingBox(null);
  }, []);

  const setActiveBoundingBoxCallback = useCallback((box: BoundingBox | null) => {
    setActiveBoundingBox(box);
    setActivePolygon(null);
    setActivePoint(null);
  }, []);

  const initializeANewPrediction = useCallback(async () => {
    return new Promise<void>(async resolve => {
      const latestACtiveImageId = await getCurrentActiveImage();

      if (latestACtiveImageId) {
        setSegmentationList(prevList => {
          const segmentation = prevList.getSegmentationByImageId(latestACtiveImageId);

          if (segmentation) {
            segmentation.clearPoints();
            segmentation.updateSegmentationResultInMemory();
          }

          resolve();
          return prevList.clone();
        });
      }
    });
  }, [getCurrentActiveImage]);

  const createOrUpdateSegmentation = useCallback(
    async (
      imageId: string,
      segmentationData?: Partial<ISegmentationDto>
    ): Promise<ISegmentationDto> => {
      const newSegmentation = new SegmentationDto(imageId, segmentationData);

      addState(SegmentationState.LOADING);

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();

          newList.addOrUpdateSegmentation(imageId, newSegmentation);

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.LOADING);

      return newSegmentation;
    },
    [addState, removeState]
  );

  const getSegmentation = useCallback(
    (imageId: string) => {
      return segmentationList.getSegmentationByImageId(imageId);
    },
    [segmentationList]
  );

  const addPoint = useCallback(
    async (imageId: string, point: Point, isPositive: boolean): Promise<void> => {
      addState(SegmentationState.ADDING);
      setActivePoint(point);

      await new Promise<void>(resolve => {
        setSegmentationList(prevSegmentationList => {
          const newList = prevSegmentationList.clone();
          let segmentation = newList.getSegmentationByImageId(imageId);

          if (!segmentation) {
            segmentation = new SegmentationDto(imageId);
            newList.addOrUpdateSegmentation(imageId, segmentation);
          }

          segmentation.addPoint(point, isPositive);

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.ADDING);
    },
    [addState, removeState]
  );

  const removePoint = useCallback(
    async (imageId: string, point: Point): Promise<boolean> => {
      addState(SegmentationState.REMOVING);
      let missionAccomplished = false;

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const updatedList = prevList.clone();
          const segmentation = updatedList.getSegmentationByImageId(imageId);

          if (segmentation) {
            missionAccomplished = segmentation.removePoint(point);
          }

          resolve();
          return missionAccomplished ? updatedList : prevList;
        });
      });

      if (
        missionAccomplished &&
        activePoint &&
        activePoint.x === point.x &&
        activePoint.y === point.y
      ) {
        setActivePoint(null);
      }

      removeState(SegmentationState.REMOVING);
      return missionAccomplished;
    },
    [activePoint, addState, removeState]
  );

  const addBoundingBox = useCallback(
    async (imageId: string, box: BoundingBox): Promise<void> => {
      addState(SegmentationState.ADDING);

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();
          let segmentation = newList.getSegmentationByImageId(imageId);

          if (!segmentation) {
            segmentation = new SegmentationDto(imageId);
            newList.addOrUpdateSegmentation(imageId, segmentation);
          }

          segmentation.addBoundingBox(box);
          setActiveBoundingBox(box);

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.ADDING);
    },
    [addState, removeState]
  );

  const removeBoundingBox = useCallback(
    async (imageId: string, id: string): Promise<boolean> => {
      addState(SegmentationState.REMOVING);
      let missionAccomplished = false;

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();
          const segmentation = newList.getSegmentationByImageId(imageId);

          if (segmentation) {
            missionAccomplished = segmentation.removeBoundingBox(id);

            if (missionAccomplished && activeBoundingBox && activeBoundingBox.id === id) {
              setActiveBoundingBox(null);
            }
          }

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.REMOVING);
      return missionAccomplished;
    },
    [activeBoundingBox, addState, removeState]
  );

  const updateBoundingBox = useCallback(
    async (
      imageId: string,
      id: string,
      propertiesToUpdate: Partial<BoundingBox>
    ): Promise<boolean> => {
      addState(SegmentationState.EDITING);
      let missionAccomplished = false;

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();
          const segmentation = newList.getSegmentationByImageId(imageId);

          if (segmentation) {
            missionAccomplished = segmentation.updateBoundingBox(id, propertiesToUpdate);

            if (missionAccomplished && activeBoundingBox && activeBoundingBox.id === id) {
              const boundingBoxToEdit = segmentation?.getBoundingBoxById(id);

              if (boundingBoxToEdit) {
                setActiveBoundingBox(boundingBoxToEdit);
              }
            }
          }

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.EDITING);
      return missionAccomplished;
    },
    [activeBoundingBox, addState, removeState]
  );

  const clearSegmentationData = useCallback(
    async (imageId?: string): Promise<void> => {
      addState(SegmentationState.REMOVING);

      const latestActiveImageId = await getCurrentActiveImage();

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();

          if (imageId) {
            const segmentation = newList.getSegmentationByImageId(imageId);

            if (segmentation) {
              segmentation.clearSegmentationData();
              if (latestActiveImageId === imageId) {
                setActivePolygon(null);
                setActivePoint(null);
                setActiveBoundingBox(null);
              }
            }
          } else {
            newList
              .getAllSegmentations()
              .forEach(segmentation => segmentation.clearSegmentationData());
          }

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.REMOVING);
    },
    [getCurrentActiveImage, addState, removeState]
  );

  const removePolygon = useCallback(
    async (imageId: string, polygonId: Polygon['id']): Promise<boolean> => {
      return new Promise(resolve => {
        let missionAccomplished = false;

        setSegmentationList(prevList => {
          const newList = prevList.clone();
          const segmentation = newList.getSegmentationByImageId(imageId);

          if (segmentation) {
            missionAccomplished = segmentation.removePolygon(polygonId);
          }

          resolve(missionAccomplished);
          return newList;
        });
      });
    },
    []
  );

  const updateActiveSegmentationResults = useCallback(async () => {
    try {
      const latestACtiveImageId = await getCurrentActiveImage();

      if (latestACtiveImageId) {
        const latestSegmentationList = await new Promise<ISegmentationListDto>(
          resolveList => {
            setSegmentationList(prevList => {
              resolveList(prevList);

              return prevList;
            });
          }
        );
        const segmentation = latestSegmentationList.getSegmentationByImageId(
          latestACtiveImageId
        );

        if (segmentation && segmentation.hasDataChanged()) {
          addState(SegmentationState.PROCESSING);

          const newResults = await updateSegmentationResult();

          if (newResults) {
            await new Promise<void>(resolve => {
              setSegmentationList(prevList => {
                const lastSegmentationData = prevList.getSegmentationByImageId(
                  latestACtiveImageId
                );

                if (lastSegmentationData) {
                  const poligons = lastSegmentationData.getPolygons();
                  const polygonsWithoutLabel = poligons.filter(polygon => !polygon.label);

                  if (polygonsWithoutLabel.length) {
                    polygonsWithoutLabel.forEach(polygon => {
                      lastSegmentationData.removePolygon(polygon.id);
                    });
                  }

                  lastSegmentationData.addPolygon({
                    points: newResults.polygon,
                  });
                  lastSegmentationData.updateSegmentationResultInMemory();
                }

                resolve();
                return prevList.clone();
              });
            });
          }

          removeState(SegmentationState.PROCESSING);
        }
      }
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'dataPrep.segmentation.errorGettingPredictionResults',
        })
      );
    }
  }, [intl, getCurrentActiveImage, addState, removeState, updateSegmentationResult]);

  const updatePolygonLabel = useCallback(
    async (
      imageId: string,
      polygonId: Polygon['id'],
      label: Polygon['label']
    ): Promise<boolean> => {
      addState(SegmentationState.LABELING);
      let missionAccomplished = false;

      await new Promise<void>(resolve => {
        setSegmentationList(prevList => {
          const newList = prevList.clone();
          const segmentation = newList.getSegmentationByImageId(imageId);

          if (segmentation) {
            missionAccomplished = segmentation.updatePolygonLabel(polygonId, label);
          }

          resolve();
          return newList;
        });
      });

      removeState(SegmentationState.LABELING);
      return missionAccomplished;
    },
    [addState, removeState]
  );

  return (
    <SegmentationContext.Provider
      value={{
        projectId,
        setProjectId,

        datasetId,
        setDatasetId,

        dataPreparationContainerId,
        setDataPreparationContainerId,

        shutdownSegmentationAPI,
        initializeSegmentationAPI,

        setInitialSegments,

        currentStates,
        addState,
        resetState,
        removeState,

        activeImageId,
        setActiveImageId: setActiveImageIdCallback,

        activePolygon,
        setActivePolygon: setActivePolygonCallback,

        activePoint,
        setActivePoint: setActivePointCallback,

        activeBoundingBox,
        setActiveBoundingBox: setActiveBoundingBoxCallback,

        activeSegmentation,
        segmentationList,
        getSegmentation,
        updateActiveSegmentationResults,
        createOrUpdateSegmentation,
        clearSegmentationData,

        addPoint,
        removePoint,

        addBoundingBox,
        removeBoundingBox,
        updateBoundingBox,

        removePolygon,
        updatePolygonLabel,

        initializeANewPrediction,
      }}
    >
      {children}
    </SegmentationContext.Provider>
  );
};

export const useSegmentationContext = () => useContext(SegmentationContext);

export default SegmentationContext;
